/**
 * RxCheckUp — API Client with JWT Authentication
 *
 * Configures axios with automatic token management:
 * - Stores access + refresh tokens in localStorage
 * - Adds Authorization header to all requests
 * - Auto-refreshes expired access tokens
 * - Redirects to /login on auth failure
 */

import axios from 'axios'

const API_BASE = '' // Same-origin; Vite proxy handles /api → backend

// ── Token Storage ────────────────────────────────────────────────────────────

const TOKEN_KEY = 'checkup_access_token'
const REFRESH_KEY = 'checkup_refresh_token'
export const USER_KEY = 'checkup_user'

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY)
}

export function setTokens(access: string, refresh: string) {
  localStorage.setItem(TOKEN_KEY, access)
  localStorage.setItem(REFRESH_KEY, refresh)
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(USER_KEY)
}

export function isLoggedIn(): boolean {
  return !!getAccessToken()
}

// ── Axios Instance ───────────────────────────────────────────────────────────

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

// Request interceptor: attach Bearer token
api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor: handle 401 by trying refresh, then redirect
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token)
    else reject(error)
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // If 401 and we haven't already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = getRefreshToken()

      // No refresh token → go to login
      if (!refreshToken) {
        clearTokens()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(api(originalRequest))
            },
            reject,
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const res = await axios.post('/api/auth/refresh', {
          refresh_token: refreshToken,
        })
        const newAccessToken = res.data.access_token
        localStorage.setItem(TOKEN_KEY, newAccessToken)
        processQueue(null, newAccessToken)
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        clearTokens()
        if (window.location.pathname !== '/demo') {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

// ── Auth API Calls ───────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  name: string
  plan: string
  practice_name: string | null
  specialty: string | null
  npi: string | null
  is_verified: boolean
  is_admin: boolean
  billing_started: boolean
  lmn_count: number
  trial_lmn_limit: number
  trial_active: boolean
  referral_code: string
  referred_by: string | null
  referral_count: number
  referral_paid_count: number
  free_months_earned: number
  free_months_used: number
  monthly_lmn_count: number
  monthly_lmn_limit: number
  bonus_lmn_count: number
  bonus_lmn_used: number
  current_month: string
  created_at: string
  trial_ends_at: string | null
}

export interface AuthResponse {
  access_token: string
  refresh_token: string
  token_type: string
  user: AuthUser
}

/** Persist user data to localStorage for quick access across page loads */
function cacheUser(user: AuthUser) {
  localStorage.setItem(USER_KEY, JSON.stringify({ ...user, loggedIn: true }))
}

export async function apiRegister(data: {
  email: string
  password: string
  name: string
  practice_name?: string
  specialty?: string
  npi?: string
  plan?: string
  billing_period?: string
  referral_code?: string
}): Promise<AuthResponse> {
  const res = await axios.post<AuthResponse>('/api/auth/register', data)
  setTokens(res.data.access_token, res.data.refresh_token)
  cacheUser(res.data.user)
  return res.data
}

export async function apiLogin(email: string, password: string): Promise<AuthResponse> {
  const res = await axios.post<AuthResponse>('/api/auth/login', { email, password })
  setTokens(res.data.access_token, res.data.refresh_token)
  cacheUser(res.data.user)
  return res.data
}

export async function apiGetMe(): Promise<AuthUser> {
  const res = await api.get<{ user: AuthUser }>('/api/auth/me')
  cacheUser(res.data.user)
  return res.data.user
}

export async function apiLogout() {
  try {
    const token = getAccessToken()
    if (token) {
      await api.post('/api/auth/logout', {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
    }
  } catch {
    // Proceed with local cleanup even if server logout fails
  }
  clearTokens()
}

export async function apiUpdateProfile(data: {
  name?: string; practice_name?: string; specialty?: string; npi?: string
}): Promise<AuthUser> {
  const res = await api.put<{ user: AuthUser }>('/api/auth/me', data)
  cacheUser(res.data.user)
  return res.data.user
}

export async function apiChangePassword(current_password: string, new_password: string): Promise<{ message: string }> {
  const res = await api.post<{ message: string }>('/api/auth/password', { current_password, new_password })
  return res.data
}

// ── Doctor Profiles API ─────────────────────────────────────────────────────

export interface PracticeLocationData {
  label: string
  address: string
  phone: string
  fax: string
}

export interface DoctorProfileData {
  id: string
  name: string
  npi: string
  specialty: string
  dea?: string
  practiceName: string
  locations: PracticeLocationData[]
  createdAt?: string
}

export interface DoctorsResponse {
  doctors: DoctorProfileData[]
  limit: number
  plan: string
}

export async function apiListDoctors(): Promise<DoctorsResponse> {
  const res = await api.get<DoctorsResponse>('/api/doctors')
  return res.data
}

export async function apiCreateDoctor(data: {
  name: string
  npi: string
  specialty: string
  dea?: string
  practice_name: string
  locations: PracticeLocationData[]
}): Promise<DoctorProfileData> {
  const res = await api.post<{ doctor: DoctorProfileData }>('/api/doctors', data)
  return res.data.doctor
}

export async function apiUpdateDoctor(id: string, data: {
  name?: string
  npi?: string
  specialty?: string
  dea?: string
  practice_name?: string
  locations?: PracticeLocationData[]
}): Promise<DoctorProfileData> {
  const res = await api.put<{ doctor: DoctorProfileData }>(`/api/doctors/${id}`, data)
  return res.data.doctor
}

export async function apiDeleteDoctor(id: string): Promise<void> {
  await api.delete(`/api/doctors/${id}`)
}

// ── Billing API ─────────────────────────────────────────────────────────────

export interface BillingStatus {
  plan: string
  billing_started: boolean
  trial_active: boolean
  lmn_count: number
  trial_lmn_limit: number
  trial_remaining: number
  monthly_lmn_count: number
  monthly_lmn_limit: number
  monthly_remaining: number
  bonus_lmn_count: number
  bonus_lmn_used: number
  bonus_remaining: number
  current_month: string | null
  stripe_customer_id: string | null
  free_months_available: number
  // Production billing state
  payment_failed: boolean
  payment_failed_at: string | null
  cancel_at_period_end: boolean
  subscription_ends_at: string | null
  current_period_end: string | null
}

export interface Invoice {
  id: string
  number: string | null
  status: string
  amount_due: number
  amount_paid: number
  currency: string
  created: number
  period_start: number | null
  period_end: number | null
  hosted_invoice_url: string | null
  invoice_pdf: string | null
  description: string
}

export interface UpcomingInvoice {
  amount_due: number
  currency: string
  period_start: number | null
  period_end: number | null
  next_payment_attempt: number | null
  lines: { description: string; amount: number; proration: boolean }[]
}

export async function apiBillingStatus(): Promise<BillingStatus> {
  const res = await api.get<BillingStatus>('/api/billing/status')
  return res.data
}

export async function apiCreateCheckout(plan: string, billing_period: string = 'monthly'): Promise<{ checkout_url: string; session_id: string }> {
  const res = await api.post<{ checkout_url: string; session_id: string }>('/api/billing/create-checkout', { plan, billing_period })
  return res.data
}

export async function apiBillingPortal(): Promise<{ portal_url: string }> {
  const res = await api.get<{ portal_url: string }>('/api/billing/portal')
  return res.data
}

export async function apiChangePlan(newPlan: string): Promise<{ success: boolean; new_plan: string; message: string; monthly_lmn_limit: number }> {
  const res = await api.post('/api/billing/change-plan', { new_plan: newPlan })
  return res.data
}

export async function apiCancelSubscription(): Promise<{ success: boolean; message: string; ends_at: string | null }> {
  const res = await api.post('/api/billing/cancel')
  return res.data
}

export async function apiReactivateSubscription(): Promise<{ success: boolean; message: string }> {
  const res = await api.post('/api/billing/reactivate')
  return res.data
}

export async function apiRetryPayment(): Promise<{ success: boolean; message: string }> {
  const res = await api.post('/api/billing/retry-payment')
  return res.data
}

export async function apiListInvoices(limit?: number): Promise<{ invoices: Invoice[] }> {
  const res = await api.get('/api/billing/invoices', { params: { limit } })
  return res.data
}

export async function apiUpcomingInvoice(newPlan?: string): Promise<UpcomingInvoice> {
  const res = await api.get('/api/billing/upcoming-invoice', { params: { new_plan: newPlan } })
  return res.data
}

// ── LMN Top-Up Packs ─────────────────────────────────────────────────────

export interface TopupPack {
  id: string
  lmns: number
  price: string
  label: string
}

export async function apiBuyTopup(pack: string): Promise<{
  checkout_url: string
  session_id: string
}> {
  const res = await api.post('/api/billing/buy-topup', { pack })
  return res.data
}

// ── LMN History API ─────────────────────────────────────────────────────────

export interface LMNHistoryItem {
  id: string
  reference_tag: string | null
  carrier: string
  denied_product: string
  diagnosis_codes: string
  generation_mode: string
  created_at: string
}

export interface LMNHistoryDetail extends LMNHistoryItem {
  letter_text: string
  citations_json: string
}

export async function apiLMNHistory(page: number = 1, limit: number = 20): Promise<{ records: LMNHistoryItem[]; total: number }> {
  const res = await api.get<{ records: LMNHistoryItem[]; total: number }>('/api/lmn-history', { params: { page, limit } })
  return res.data
}

export async function apiLMNHistoryDetail(id: string): Promise<LMNHistoryDetail> {
  const res = await api.get<{ record: LMNHistoryDetail }>(`/api/lmn-history/${id}`)
  return res.data.record
}

// ── Appeals Dashboard API ───────────────────────────────────────────────────

export interface AppealItem {
  id: string
  reference_tag: string | null
  carrier: string
  plan_type: string
  denied_product: string
  denial_reason: string
  icd10_codes: string[]
  outcome: string
  outcome_updated_at: string | null
  payer_denial_reason: string | null
  payer_denial_code: string | null
  appeal_notes: string | null
  improved_from_id: string | null
  generation_mode: string
  created_at: string
  has_feedback: boolean
  claim_amount: number | null
  recovered_amount: number | null
  estimated_annual_value: number | null
}

export interface AppealDetail extends AppealItem {
  letter_text: string
  confidence_note: string | null
  citations: { number: number; source: string; title: string; url?: string }[]
  feedback: {
    id: string
    payer_reason: string
    payer_requirements: string | null
    missing_documentation: string | null
    lesson_learned: string | null
    should_include_next_time: string | null
    created_at: string
  }[]
}

export interface AppealsDashboardData {
  total_lmns: number
  pending: number
  submitted: number
  approved: number
  denied: number
  win_rate: number | null
  feedback_count: number
  top_denied_carriers: { carrier: string; count: number }[]
  top_denied_drugs: { drug: string; count: number }[]
  recent_outcomes: {
    id: string
    carrier: string
    denied_product: string
    denial_reason: string
    outcome: string
    outcome_updated_at: string | null
    payer_denial_reason: string | null
    created_at: string
  }[]
}

export async function apiAppealsDashboard(): Promise<AppealsDashboardData> {
  const res = await api.get<AppealsDashboardData>('/api/appeals/dashboard')
  return res.data
}

export async function apiAppealsHistory(params?: {
  outcome?: string; carrier?: string; page?: number; limit?: number
}): Promise<{ appeals: AppealItem[]; total: number; page: number; pages: number }> {
  const res = await api.get('/api/appeals/history', { params })
  return res.data
}

export async function apiAppealDetail(id: string): Promise<AppealDetail> {
  const res = await api.get<AppealDetail>(`/api/appeals/${id}/detail`)
  return res.data
}

export async function apiUpdateOutcome(id: string, data: {
  outcome: string; appeal_notes?: string
}): Promise<{ message: string }> {
  const res = await api.put(`/api/appeals/${id}/outcome`, data)
  return res.data
}

export async function apiSubmitDenialFeedback(id: string, data: {
  payer_reason: string; payer_requirements?: string; missing_documentation?: string; payer_denial_code?: string
}): Promise<{ message: string; lesson_learned: string; improvements: string }> {
  const res = await api.post(`/api/appeals/${id}/feedback`, data)
  return res.data
}

export async function apiAppealsInsights(params?: {
  carrier?: string; drug?: string
}): Promise<{ insights: any[]; total: number }> {
  const res = await api.get('/api/appeals/insights', { params })
  return res.data
}

// ── Appeals Search API ──────────────────────────────────────────────────────

export interface AppealsSearchParams {
  q?: string
  carrier?: string
  denied_product?: string
  denial_reason?: string
  outcome?: string
  from_date?: string
  to_date?: string
  sort_by?: string
  sort_order?: string
  page?: number
  per_page?: number
}

export interface AppealsSearchResult {
  appeals: AppealItem[]
  total: number
  page: number
  per_page: number
  pages: number
}

export async function apiAppealsSearch(params: AppealsSearchParams): Promise<AppealsSearchResult> {
  const res = await api.get<AppealsSearchResult>('/api/appeals/search', { params })
  return res.data
}

export async function apiUpdateReferenceTag(id: string, reference_tag: string | null): Promise<{ id: string; reference_tag: string | null }> {
  const res = await api.patch(`/api/appeals/${id}/tag`, { reference_tag })
  return res.data
}

// ── ROI Dashboard API ────────────────────────────────────────────────────────

export interface ROIMonthlyTrend {
  month: string
  label: string
  recovered: number
}

export interface ROIDashboardData {
  total_claims_value: number
  total_recovered: number
  recovery_rate_pct: number
  total_appeals: number
  won_appeals: number
  win_rate_pct: number
  avg_recovery_per_appeal: number
  subscription_cost: number
  roi_multiple: number
  top_recovered_drugs: { drug: string; recovered: number }[]
  top_recovered_carriers: { carrier: string; recovered: number }[]
  monthly_trend: ROIMonthlyTrend[]
  pending_value: number
}

export async function apiGetROIDashboard(period: string = 'month'): Promise<ROIDashboardData> {
  const res = await api.get<ROIDashboardData>('/api/appeals/roi-dashboard', { params: { period } })
  return res.data
}

export async function apiUpdateAppealFinancials(recordId: string, data: {
  claim_amount?: number; recovered_amount?: number; estimated_annual_value?: number
}): Promise<{ id: string; claim_amount: number | null; recovered_amount: number | null; estimated_annual_value: number | null }> {
  const res = await api.put(`/api/appeals/${recordId}/financials`, data)
  return res.data
}

// ── Appeal Workflow / Stage Tracking API ─────────────────────────────────────

export interface AppealStage {
  id: string
  label: string
  description: string
}

export interface AppealTimelineEvent {
  id: string
  stage: string
  stage_label: string
  action: string
  notes: string | null
  created_at: string | null
}

export interface AppealStageWithStatus extends AppealStage {
  status: 'completed' | 'current' | 'future'
  events: AppealTimelineEvent[]
}

export interface AppealTimelineData {
  record_id: string
  current_stage: string | null
  current_stage_label: string | null
  stages: AppealStageWithStatus[]
  timeline: AppealTimelineEvent[]
}

export interface AppealGuidance {
  stage: string
  carrier: string
  next_action: string
  guidance: string
  tips: string[]
  deadline_days: number | null
  next_stage: string | null
  next_stage_label: string | null
}

export interface AppealChecklistItem {
  id: string
  label: string
  description: string
  required: boolean
}

export interface AppealNextStepsData {
  record_id: string
  current_stage: string | null
  current_stage_label: string | null
  guidance: AppealGuidance | null
  checklist: AppealChecklistItem[]
  next_stage: {
    current_stage: string
    next_stage: string | null
    next_stage_label?: string
    can_advance: boolean
    message?: string
  }
  is_resolved: boolean
}

export interface AdvanceAppealResult {
  message: string
  new_stage: string
  new_stage_label?: string
  action: string
  record_id: string
  guidance?: AppealGuidance
  checklist?: AppealChecklistItem[]
}

export async function apiAppealStages(): Promise<{ stages: AppealStage[] }> {
  const res = await api.get('/api/appeals/stages')
  return res.data
}

export async function apiAppealTimeline(recordId: string): Promise<AppealTimelineData> {
  const res = await api.get<AppealTimelineData>(`/api/appeals/${recordId}/timeline`)
  return res.data
}

export async function apiAdvanceAppeal(recordId: string, data: {
  action: 'denied' | 'approved'; notes?: string
}): Promise<AdvanceAppealResult> {
  const res = await api.post(`/api/appeals/${recordId}/advance`, data)
  return res.data
}

export async function apiAppealNextSteps(recordId: string): Promise<AppealNextStepsData> {
  const res = await api.get<AppealNextStepsData>(`/api/appeals/${recordId}/next-steps`)
  return res.data
}

// ── P2P Prep Sheet API ──────────────────────────────────────────────────────

export interface P2PTalkingPoint {
  point: string
  detail: string
}

export interface P2PObjectionResponse {
  objection: string
  response: string
}

export interface P2PClinicalData {
  fda_approval: string
  key_trial: string
  guidelines: string[]
  patient_metrics: string
}

export interface P2PMetadata {
  carrier: string
  drug: string
  brand_names: string[]
  denial_reason: string
  icd10_codes: string[]
  date: string
  reference_tag: string
  plan_type: string
}

export interface P2PData {
  opening_statement: string
  talking_points: P2PTalkingPoint[]
  objections_responses: P2PObjectionResponse[]
  clinical_data: P2PClinicalData
  carrier_tips: string[]
  closing_statement: string
  if_unsuccessful: string[]
  metadata: P2PMetadata
}

export interface P2PResponse {
  p2p_data: P2PData
  text: string
  html: string
}

export async function apiGenerateP2PFromRecord(recordId: string): Promise<P2PResponse> {
  const res = await api.post<P2PResponse>(`/api/appeals/${recordId}/p2p-prep`)
  return res.data
}

export async function apiGenerateP2PStandalone(data: {
  carrier: string
  drug: string
  denial_reason: string
  icd10_codes: string[]
  prior_treatments?: string[]
  plan_type?: string
  provider_info?: Record<string, string>
}): Promise<P2PResponse> {
  const res = await api.post<P2PResponse>('/api/appeals/p2p-prep/generate', data)
  return res.data
}

// ── Appeal Deadlines API ────────────────────────────────────────────────────

export interface DeadlineStatus {
  status: 'overdue' | 'critical' | 'warning' | 'ok' | 'no_deadline'
  days_remaining: number | null
  color: string
  label: string
}

export interface DeadlineItem extends DeadlineStatus {
  id: string
  reference_tag: string | null
  carrier: string
  denied_product: string
  denial_reason: string
  outcome: string
  appeal_level: string
  appeal_level_label: string
  appeal_deadline: string | null
  denial_date: string | null
  appeal_submitted_date: string | null
  deadline_reminder_sent: boolean
  appeal_notes: string | null
  created_at: string
}

export interface DeadlineSummary {
  overdue: number
  due_this_week: number
  due_two_weeks: number
  total_active: number
}

export interface DeadlinesResponse {
  deadlines: DeadlineItem[]
  summary: DeadlineSummary
}

export interface CarrierDeadlineRules {
  carrier_key: string
  carrier_name: string
  appeals_fax: string | null
  appeals_phone: string | null
  levels: Record<string, { days: number; urgent_days?: number; notes: string; label: string }>
}

export async function apiGetDeadlines(): Promise<DeadlinesResponse> {
  const res = await api.get<DeadlinesResponse>('/api/appeals/deadlines')
  return res.data
}

export async function apiGetUpcomingDeadlines(days: number = 7): Promise<DeadlinesResponse & { days_ahead: number }> {
  const res = await api.get('/api/appeals/deadlines/upcoming', { params: { days } })
  return res.data
}

export async function apiUpdateDeadline(recordId: string, data: {
  denial_date?: string
  appeal_level?: string
  appeal_submitted_date?: string
  appeal_notes?: string
  is_urgent?: boolean
}): Promise<DeadlineItem> {
  const res = await api.patch<DeadlineItem>(`/api/appeals/${recordId}/deadline`, data)
  return res.data
}

export async function apiEscalateAppeal(recordId: string): Promise<{
  id: string
  carrier: string
  denied_product: string
  previous_level: string
  appeal_level: string
  appeal_level_label: string
  denial_date: string | null
  appeal_deadline: string | null
} & DeadlineStatus> {
  const res = await api.post(`/api/appeals/${recordId}/escalate`)
  return res.data
}

export async function apiGetDeadlineRules(carrier: string): Promise<CarrierDeadlineRules> {
  const res = await api.get<CarrierDeadlineRules>(`/api/appeals/deadline-rules/${encodeURIComponent(carrier)}`)
  return res.data
}

export async function apiListDeadlineRules(): Promise<{ carriers: { key: string; name: string }[] }> {
  const res = await api.get('/api/appeals/deadline-rules')
  return res.data
}

// ── Practice Owner Report API ────────────────────────────────────────────────

export interface PracticeReportExecutiveSummary {
  total_appeals_filed: number
  appeals_won: number
  appeals_lost: number
  appeals_pending: number
  win_rate_pct: number
  total_claim_value: number
  total_recovered: number
  recovery_rate_pct: number
  pending_pipeline_value: number
  subscription_cost: number
  roi_multiple: number
  time_saved_hours: number
}

export interface PracticeReportCarrier {
  carrier: string
  appeals: number
  won: number
  win_rate: number
  recovered: number
  avg_days_to_resolution: number | null
}

export interface PracticeReportDrug {
  drug: string
  appeals: number
  won: number
  win_rate: number
  recovered: number
}

export interface PracticeReportStaff {
  name: string
  appeals_handled: number
  won: number
  win_rate: number
  recovered: number
}

export interface PracticeReportMoM {
  appeals_change_pct: number
  win_rate_change_pct: number
  recovery_change_pct: number
}

export interface PracticeReport {
  report_title: string
  period: string
  period_type: string
  generated_at: string
  practice_name: string | null
  executive_summary: PracticeReportExecutiveSummary
  carrier_breakdown: PracticeReportCarrier[]
  drug_breakdown: PracticeReportDrug[]
  staff_performance: PracticeReportStaff[]
  month_over_month: PracticeReportMoM
  highlights: string[]
}

export async function apiGetPracticeReport(period: string = 'month'): Promise<PracticeReport> {
  const res = await api.get<PracticeReport>('/api/appeals/practice-report', { params: { period } })
  return res.data
}

// ── Password Reset API ──────────────────────────────────────────────────────

export async function apiForgotPassword(email: string): Promise<{ message: string }> {
  const res = await api.post<{ message: string }>('/api/auth/forgot-password', { email })
  return res.data
}

export async function apiResetPassword(token: string, new_password: string): Promise<{ message: string }> {
  const res = await api.post<{ message: string }>('/api/auth/reset-password', { token, new_password })
  return res.data
}

// ── Admin API ────────────────────────────────────────────────────────────────

export interface AdminDashboardData {
  total_users: number
  starter_count: number
  professional_count: number
  professional_plus_count?: number
  enterprise_count: number
  new_this_month: number
  new_this_week: number
  paying_users: number
  trial_users: number
  total_lmns_generated: number
  lmns_this_month: number
  open_tickets: number
  active_7d: number
  churn_risk: number
}

export interface AdminRevenueData {
  mrr: number
  ytd_revenue: number
  annual_projection: number
  paying_users: number
  arpu: number
  plan_breakdown: { plan: string; count: number; price: number; revenue: number }[]
  monthly_growth: { month: string; new_users: number }[]
}

export interface AdminUserItem {
  id: string
  email: string
  name: string
  plan: string
  is_active: boolean
  is_verified: boolean
  is_admin: boolean
  billing_started: boolean
  lmn_count: number
  monthly_lmn_count: number
  monthly_lmn_limit: number
  trial_active: boolean
  practice_name: string | null
  specialty: string | null
  referral_code: string
  referral_count: number
  stripe_customer_id: string | null
  created_at: string
  trial_ends_at: string | null
  last_login: string | null
}

export interface AdminTicketItem {
  id: string
  subject: string
  message: string
  source: string
  status: string
  priority: string
  category: string | null
  admin_notes: string | null
  assigned_to: string | null
  created_at: string
  updated_at: string
  resolved_at: string | null
  user?: { id: string; email: string; name: string; plan: string }
}

export interface AdminInsightsData {
  total_lmns: number
  lmns_this_month: number
  lmns_this_quarter: number
  quarter_label: string
  top_drugs: { name: string; count: number }[]
  top_carriers: { name: string; count: number }[]
  top_denial_reasons: { name: string; count: number }[]
  daily_volume: { date: string; count: number }[]
  generation_modes: { mode: string; count: number }[]
  denial_hotspots: { carrier: string; drug: string; reason: string; count: number }[]
  carrier_win_rates: { carrier: string; approved: number; denied: number; total: number; win_rate: number }[]
  drug_win_rates: { drug: string; approved: number; denied: number; total: number; win_rate: number }[]
  outcome_summary: Record<string, number>
  plan_type_breakdown: { type: string; count: number }[]
  specialty_breakdown: { specialty: string; count: number }[]
  appeal_level_distribution: { level: string; count: number }[]
  engagement: { active_30d: number; active_90d: number; total_accounts: number; engagement_30d_pct: number; engagement_90d_pct: number }
  accounts: {
    id: string; name: string; email: string; practice_name: string; plan: string
    monthly_limit: number; team_size: number; month_count: number; quarter_count: number
    lifetime_count: number; is_active: boolean; last_login: string; created_at: string
  }[]
}

export async function apiAdminDashboard(): Promise<AdminDashboardData> {
  const res = await api.get<AdminDashboardData>('/api/admin/dashboard')
  return res.data
}

export async function apiAdminRevenue(): Promise<AdminRevenueData> {
  const res = await api.get<AdminRevenueData>('/api/admin/revenue')
  return res.data
}

export async function apiAdminUsers(params?: {
  page?: number; limit?: number; plan?: string; billing?: string; search?: string
}): Promise<{ users: AdminUserItem[]; total: number; page: number; pages: number }> {
  const res = await api.get('/api/admin/users', { params })
  return res.data
}

export async function apiAdminUserDetail(id: string): Promise<AdminUserItem & { doctor_count: number; total_lmns: number; ticket_count: number }> {
  const res = await api.get(`/api/admin/users/${id}`)
  return res.data
}

export async function apiAdminUpdateUser(id: string, data: Record<string, unknown>): Promise<AdminUserItem> {
  const res = await api.put(`/api/admin/users/${id}`, data)
  return res.data
}

export async function apiAdminTickets(params?: {
  status?: string; priority?: string; page?: number; limit?: number
}): Promise<{ tickets: AdminTicketItem[]; total: number }> {
  const res = await api.get('/api/admin/tickets', { params })
  return res.data
}

export async function apiAdminUpdateTicket(id: string, data: Record<string, unknown>): Promise<AdminTicketItem> {
  const res = await api.put(`/api/admin/tickets/${id}`, data)
  return res.data
}

export async function apiAdminInsights(): Promise<AdminInsightsData> {
  const res = await api.get<AdminInsightsData>('/api/admin/insights')
  return res.data
}

// ── Market Intelligence API ──────────────────────────────────────────────────

export interface MarketIntelOverview {
  total_lmn_records: number; total_with_outcomes: number
  total_drugs_seen: number; total_carriers_seen: number; total_specialties_seen: number
  drugs_in_database: number; carriers_in_database: number
  data_since: string; last_activity: string
  monthly_trend: { month: string; lmn_count: number; outcome_count: number }[]
}
export interface PharmaReport {
  drug_name: string; brand_names: string[]; drug_class: string
  total_lmns: number
  carriers_denying: { carrier: string; count: number; denial_reasons: string[] }[]
  outcomes: { approved: number; denied: number; pending: number; submitted: number; win_rate: number }
  top_denial_reason: string
  avg_appeal_level: string
  monthly_volume: { month: string; count: number }[]
}
export interface PayerBenchmark {
  carrier_name: string; total_lmns: number
  total_outcomes: number; approved: number; denied: number; win_rate: number
  top_denied_drugs: { drug: string; count: number }[]
  denial_reason_breakdown: { reason: string; count: number; pct: number }[]
  plan_type_split: { type: string; count: number }[]
  monthly_volume: { month: string; count: number }[]
}
export interface DenialTrend {
  drug: string; carrier: string; denial_reason: string
  volume_30d: number; volume_60d: number; volume_90d: number
  trend: string; velocity_change_pct: number
}
export interface SpecialtyInsight {
  specialty: string; total_lmns: number; total_providers: number
  top_drugs: { drug: string; count: number }[]
  top_carriers: { carrier: string; count: number }[]
  outcomes: { approved: number; denied: number; win_rate: number }
  avg_lmns_per_provider: number
}
export interface AnnualReport {
  year: number; total_lmns: number; total_unique_drugs: number; total_unique_carriers: number
  overall_win_rate: number
  top_10_denied_drugs: { drug: string; count: number }[]
  top_10_denying_carriers: { carrier: string; count: number }[]
  top_denial_reasons: { reason: string; count: number }[]
  highest_win_rate_carrier: { carrier: string; win_rate: number; total: number } | null
  lowest_win_rate_carrier: { carrier: string; win_rate: number; total: number } | null
  highest_win_rate_drug: { drug: string; win_rate: number; total: number } | null
  lowest_win_rate_drug: { drug: string; win_rate: number; total: number } | null
  monthly_breakdown: { month: string; lmn_count: number; approved: number; denied: number }[]
  plan_type_distribution: { type: string; count: number }[]
  specialty_distribution: { specialty: string; count: number }[]
  appeal_level_distribution: { level: string; count: number }[]
}
export interface DataExportStats {
  total_anonymized_records: number
  date_range: { start: string; end: string }
  drug_coverage: number; carrier_coverage: number
  fields_available: string[]
  sample_record: Record<string, string>
}

export const apiMarketIntelOverview = async (): Promise<MarketIntelOverview> =>
  (await api.get('/api/admin/market-intel/overview')).data
export const apiMarketIntelPharma = async (params?: { drug?: string; min_volume?: number }): Promise<{ reports: PharmaReport[]; total_drugs: number }> =>
  (await api.get('/api/admin/market-intel/pharma-reports', { params })).data
export const apiMarketIntelPayer = async (params?: { carrier?: string }): Promise<{ benchmarks: PayerBenchmark[]; total_carriers: number }> =>
  (await api.get('/api/admin/market-intel/payer-benchmarks', { params })).data
export const apiMarketIntelTrends = async (): Promise<{ trends: DenialTrend[]; alerts: DenialTrend[]; total_combos: number; alert_count: number }> =>
  (await api.get('/api/admin/market-intel/denial-trends')).data
export const apiMarketIntelSpecialty = async (): Promise<{ specialties: SpecialtyInsight[]; total_specialties: number }> =>
  (await api.get('/api/admin/market-intel/specialty-insights')).data
export const apiMarketIntelAnnual = async (): Promise<AnnualReport> =>
  (await api.get('/api/admin/market-intel/annual-report')).data
export const apiMarketIntelExportStats = async (): Promise<DataExportStats> =>
  (await api.get('/api/admin/market-intel/data-export-stats')).data

// ── Market Intelligence Products API ─────────────────────────────────────────

export interface MarketIntelProductThreshold {
  required: number
  current: number
  met: boolean
}

export interface MarketIntelProductBuyer {
  segment: string
  description: string
  estimated_market_size: string
}

export interface MarketIntelPricingTier {
  tier_name: string
  price: number
  billing_period: string
  features: string[]
}

export interface MarketIntelProduct {
  product_id: string
  product_name: string
  description: string
  status: string
  enabled: boolean
  readiness_pct: number
  deployable: boolean
  thresholds: Record<string, MarketIntelProductThreshold>
  estimated_days_to_ready: number | null
  pricing_summary: string
  pricing_tiers: MarketIntelPricingTier[]
  target_buyer_count: number
  target_buyers: MarketIntelProductBuyer[]
  top_target: string | null
  delivery_methods: string[]
}

export interface MarketIntelProductReadiness {
  products: MarketIntelProduct[]
  summary: {
    total_products: number
    products_ready: number
    estimated_total_annual_revenue: number
    total_target_segments: number
  }
}

export const apiMarketIntelProductReadiness = async (): Promise<MarketIntelProductReadiness> =>
  (await api.get('/api/admin/market-intel/product-readiness')).data

export const apiMarketIntelToggleProduct = async (productId: string): Promise<{ product_id: string; enabled: boolean; message: string }> =>
  (await api.post(`/api/admin/market-intel/products/${productId}/toggle`)).data

export const apiMarketIntelUpdateProductStatus = async (productId: string, status: string): Promise<{ product_id: string; status: string; message: string }> =>
  (await api.put(`/api/admin/market-intel/products/${productId}/status`, { status })).data

export async function apiSubmitSupportTicket(data: {
  subject: string; message: string; source?: string; category?: string
}): Promise<{ message: string; ticket_id: string }> {
  const res = await api.post('/api/support/ticket', data)
  return res.data
}

// ── Team Management API ──────────────────────────────────────────────────────

export interface TeamMember {
  id: string
  name: string
  email: string
  created_at: string
  last_login: string | null
}

export interface TeamData {
  owner: { id: string; name: string; email: string }
  members: TeamMember[]
  total_users: number
  max_users: number
  plan: string
}

export async function apiGetTeam(): Promise<TeamData> {
  const res = await api.get('/api/team')
  return res.data
}

export async function apiAddTeamMember(data: {
  name: string; email: string; password: string
}): Promise<{ message: string; member: TeamMember; total_users: number; max_users: number }> {
  const res = await api.post('/api/team', data)
  return res.data
}

export async function apiUpdateTeamMember(memberId: string, data: {
  name?: string; email?: string; password?: string
}): Promise<{ message: string; member: TeamMember }> {
  const res = await api.put(`/api/team/${memberId}`, data)
  return res.data
}

export async function apiRemoveTeamMember(memberId: string): Promise<{ message: string }> {
  const res = await api.delete(`/api/team/${memberId}`)
  return res.data
}

// ── Payer Directory API ──────────────────────────────────────────────────────

export interface PayerCarrierSummary {
  key: string
  carrier_name: string
  parent_company: string
  appeals_fax: string
  appeals_phone: string
  peer_to_peer_phone: string
}

export interface PayerAppealContact {
  fax: string
  phone: string
  mailing_address: string
  hours: string
  turnaround: string
}

export interface PayerCarrierDetail {
  key: string
  carrier_name: string
  parent_company: string
  appeals: {
    medical: PayerAppealContact
    pharmacy: PayerAppealContact
  }
  peer_to_peer: {
    phone: string
    scheduling_notes: string
    typical_wait: string
  }
  prior_auth: {
    phone: string
    fax: string
    portal: string
  }
  provider_relations: {
    phone: string
    portal: string
  }
  state_filing_contacts: {
    notes: string
  }
}

export interface PayerCoverSheet {
  to: string
  to_fax: string
  to_address: string
  from_name: string
  from_fax: string
  re: string
  date: string
  pages: string
  carrier_key: string
  carrier_name: string
  appeal_type: string
}

export async function apiPayerDirectoryList(): Promise<{ carriers: PayerCarrierSummary[]; total: number }> {
  const res = await api.get('/api/payer-directory')
  return res.data
}

export async function apiPayerDirectorySearch(q: string): Promise<{ carriers: PayerCarrierSummary[]; total: number }> {
  const res = await api.get('/api/payer-directory/search', { params: { q } })
  return res.data
}

export async function apiPayerDirectoryDetail(carrierKey: string): Promise<PayerCarrierDetail> {
  const res = await api.get(`/api/payer-directory/${carrierKey}`)
  return res.data
}

export async function apiPayerDirectoryAppeals(carrierKey: string): Promise<{
  key: string; carrier_name: string; appeals: PayerCarrierDetail['appeals'];
  peer_to_peer: PayerCarrierDetail['peer_to_peer']; state_filing_contacts: PayerCarrierDetail['state_filing_contacts']
}> {
  const res = await api.get(`/api/payer-directory/${carrierKey}/appeals`)
  return res.data
}

export async function apiPayerDirectoryCoverSheet(
  carrierKey: string,
  params?: { doctor_name?: string; doctor_fax?: string; drug_name?: string; patient_ref?: string; appeal_type?: string }
): Promise<PayerCoverSheet> {
  const res = await api.get(`/api/payer-directory/${carrierKey}/cover-sheet`, { params })
  return res.data
}

// ── Deadline Reminders API ────────────────────────────────────────────────────

export interface MyDeadlineItem {
  id: string
  reference_tag: string | null
  carrier: string
  denied_product: string
  denial_reason: string
  outcome: string
  appeal_level: string
  appeal_level_label: string
  appeal_deadline: string | null
  denial_date: string | null
  appeal_submitted_date: string | null
  days_remaining: number
  urgency_level: 'overdue' | 'critical' | 'urgent' | 'upcoming' | 'ok'
  deadline_reminder_sent: boolean
  appeal_notes: string | null
  assigned_to_id: string | null
  assigned_to_name: string | null
  created_at: string
  status: string
  color: string
  label: string
}

export async function apiGetMyDeadlines(): Promise<{ deadlines: MyDeadlineItem[]; total: number }> {
  const res = await api.get('/api/appeals/my-deadlines')
  return res.data
}

export async function apiCheckDeadlines(): Promise<{
  checked_at: string
  total_records_checked: number
  reminders_sent: number
  reminders_failed: number
  details: { record_id: string; user_email: string; urgency: string; status: string }[]
}> {
  const res = await api.post('/api/appeals/check-deadlines')
  return res.data
}

// ── Bulk Operations API ──────────────────────────────────────────────────────

export async function apiExportAppeals(filters?: {
  format?: string
  carrier?: string
  outcome?: string
  date_from?: string
  date_to?: string
}): Promise<Blob> {
  const res = await api.get('/api/appeals/export', {
    params: { format: 'csv', ...filters },
    responseType: 'blob',
  })
  return res.data
}

export async function apiExportAppealsJSON(filters?: {
  carrier?: string
  outcome?: string
  date_from?: string
  date_to?: string
}): Promise<{ appeals: Record<string, unknown>[]; total: number }> {
  const res = await api.get('/api/appeals/export', {
    params: { format: 'json', ...filters },
  })
  return res.data
}

export async function apiBulkUpdateAppeals(
  recordIds: string[],
  updates: { outcome?: string; appeal_level?: string }
): Promise<{ message: string; updated_count: number; requested_count: number }> {
  const res = await api.post('/api/appeals/bulk-update', {
    record_ids: recordIds,
    ...updates,
  })
  return res.data
}

// ── Staff Assignment API ─────────────────────────────────────────────────────

export async function apiAssignAppeal(
  recordId: string,
  assignedToId: string | null
): Promise<{ id: string; assigned_to_id: string | null; assigned_to_name: string | null; message: string }> {
  const res = await api.put(`/api/appeals/${recordId}/assign`, { assigned_to_id: assignedToId })
  return res.data
}

export interface StaffWorkloadItem {
  id: string
  name: string
  email: string
  total_assigned: number
  pending_count: number
  submitted_count: number
  approved_count: number
  denied_count: number
  win_rate: number | null
  total_recovered: number
  upcoming_deadlines: number
}

export async function apiGetStaffWorkload(): Promise<{ staff: StaffWorkloadItem[]; total_staff: number }> {
  const res = await api.get('/api/appeals/staff-workload')
  return res.data
}

// ── Notifications API ────────────────────────────────────────────────────────

export interface NotificationItem {
  id: string
  user_id: string
  type: string  // deadline_warning | usage_alert | appeal_update | system | billing
  title: string
  message: string
  read: boolean
  action_url: string | null
  created_at: string
}

export async function apiGetNotifications(): Promise<{ notifications: NotificationItem[] }> {
  const res = await api.get<{ notifications: NotificationItem[] }>('/api/notifications')
  return res.data
}

export async function apiGetUnreadCount(): Promise<{ unread_count: number }> {
  const res = await api.get<{ unread_count: number }>('/api/notifications/unread-count')
  return res.data
}

export async function apiMarkNotificationRead(id: string): Promise<void> {
  await api.put(`/api/notifications/${id}/read`)
}

export async function apiMarkAllNotificationsRead(): Promise<void> {
  await api.put('/api/notifications/read-all')
}

export async function apiDeleteNotification(id: string): Promise<void> {
  await api.delete(`/api/notifications/${id}`)
}

// ── Saved Templates (Favorites) API ─────────────────────────────────────────

export interface SavedTemplateData {
  id: string
  name: string
  carrier: string
  plan_type: string
  plan_name: string | null
  icd10_codes: string[]
  denied_product: string
  denial_reason: string
  prior_treatments: string[]
  reference_tag: string | null
  created_at: string
  last_used_at: string
}

export async function apiGetSavedTemplates(): Promise<{ templates: SavedTemplateData[] }> {
  const res = await api.get<{ templates: SavedTemplateData[] }>('/api/saved-templates')
  return res.data
}

export async function apiSaveTemplate(data: {
  name: string
  carrier: string
  plan_type: string
  plan_name?: string
  icd10_codes: string[]
  denied_product: string
  denial_reason: string
  prior_treatments?: string[]
  reference_tag?: string
}): Promise<SavedTemplateData> {
  const res = await api.post<SavedTemplateData>('/api/saved-templates', data)
  return res.data
}

export async function apiDeleteSavedTemplate(id: string): Promise<{ message: string }> {
  const res = await api.delete<{ message: string }>(`/api/saved-templates/${id}`)
  return res.data
}

export async function apiUseSavedTemplate(id: string): Promise<{ id: string; name: string; last_used_at: string }> {
  const res = await api.put<{ id: string; name: string; last_used_at: string }>(`/api/saved-templates/${id}/use`)
  return res.data
}

// ── Bulk LMN Generation API ──────────────────────────────────────────────────

export interface BulkLMNItemRequest {
  carrier: string
  plan_type: string
  icd10_codes: string[]
  denied_product: string
  denial_reason: string
  reference_tag?: string
}

export interface BulkLMNResultItem {
  index: number
  status: 'success' | 'error'
  record_id?: string
  reference_tag?: string | null
  denied_product?: string
  carrier?: string
  error?: string
}

export interface BulkLMNResponse {
  total: number
  successful: number
  failed: number
  results: BulkLMNResultItem[]
  usage_alert?: { type: string; remaining: number; limit: number; plan: string; message: string } | null
}

export async function apiBulkGenerate(requests: BulkLMNItemRequest[]): Promise<BulkLMNResponse> {
  const res = await api.post<BulkLMNResponse>('/api/bulk-generate', { requests })
  return res.data
}

// ── Webhooks (Enterprise) ─────────────────────────────────────────────────────

export interface Webhook {
  id: string
  url: string
  events: string[]
  active: boolean
  description: string | null
  created_at: string | null
  last_triggered_at: string | null
  failure_count: number
  secret?: string  // Only present on create response
}

export async function apiGetWebhooks(): Promise<{ webhooks: Webhook[] }> {
  const res = await api.get<{ webhooks: Webhook[] }>('/api/webhooks')
  return res.data
}

export async function apiCreateWebhook(data: {
  url: string
  events: string[]
  description?: string
}): Promise<Webhook & { secret: string }> {
  const res = await api.post<Webhook & { secret: string }>('/api/webhooks', data)
  return res.data
}

export async function apiUpdateWebhook(id: string, data: {
  url?: string
  events?: string[]
  active?: boolean
  description?: string
}): Promise<Webhook> {
  const res = await api.put<Webhook>(`/api/webhooks/${id}`, data)
  return res.data
}

export async function apiDeleteWebhook(id: string): Promise<{ message: string }> {
  const res = await api.delete<{ message: string }>(`/api/webhooks/${id}`)
  return res.data
}

export async function apiTestWebhook(id: string): Promise<{ message: string; url: string }> {
  const res = await api.post<{ message: string; url: string }>(`/api/webhooks/${id}/test`)
  return res.data
}

// ── API Keys (Enterprise) ─────────────────────────────────────────────────────

export interface APIKeyInfo {
  id: string
  key_prefix: string
  name: string
  permissions: string[]
  active: boolean
  created_at: string | null
  last_used_at: string | null
  expires_at: string | null
  key?: string  // Only present on create response (full key shown once)
}

export async function apiGetAPIKeys(): Promise<{ api_keys: APIKeyInfo[] }> {
  const res = await api.get<{ api_keys: APIKeyInfo[] }>('/api/api-keys')
  return res.data
}

export async function apiCreateAPIKey(data: {
  name: string
  permissions: string[]
}): Promise<APIKeyInfo & { key: string }> {
  const res = await api.post<APIKeyInfo & { key: string }>('/api/api-keys', data)
  return res.data
}

export async function apiUpdateAPIKey(id: string, data: {
  name?: string
  permissions?: string[]
  active?: boolean
}): Promise<APIKeyInfo> {
  const res = await api.put<APIKeyInfo>(`/api/api-keys/${id}`, data)
  return res.data
}

export async function apiDeleteAPIKey(id: string): Promise<{ message: string }> {
  const res = await api.delete<{ message: string }>(`/api/api-keys/${id}`)
  return res.data
}

// ── Branding / White-Label API ───────────────────────────────────────────────

export interface BrandingData {
  id?: string | null
  practice_logo_url: string | null
  primary_color: string
  accent_color: string
  practice_display_name: string | null
  custom_footer_text: string | null
  hide_rxcheckup_branding: boolean
  custom_email_header: string | null
  letterhead_enabled: boolean
  letterhead_html: string | null
}

export async function apiGetBranding(): Promise<BrandingData> {
  const res = await api.get<{ branding: BrandingData }>('/api/branding')
  return res.data.branding
}

export async function apiUpdateBranding(data: Partial<BrandingData>): Promise<BrandingData> {
  const res = await api.put<{ branding: BrandingData; message: string }>('/api/branding', data)
  return res.data.branding
}

export async function apiUploadLogo(file: File): Promise<{ logo_url: string; message: string }> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await api.post<{ logo_url: string; message: string }>('/api/branding/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export async function apiDeleteLogo(): Promise<{ message: string }> {
  const res = await api.delete<{ message: string }>('/api/branding/logo')
  return res.data
}

// ── Intelligence API ──────────────────────────────────────────────────────────
// NOTE: Intelligence types and API functions are defined here but only wired up
// when the corresponding UI features are activated per plan. They are kept as the
// contract for the /api/intelligence/* backend endpoints.
//
// To keep the bundle lean, they are lazy-exported: import them only in the
// components that actually call them (e.g., IntelligenceHub, SmartAlertsPanel).

export interface ApprovalPrediction {
  score: number
  risk_level: string
  factors: Array<{ name: string; impact: number; direction: string; detail: string }>
  missing_critical: string[]
  recommendations: string[]
  similar_outcomes: { total: number; approved: number; denied: number }
  estimated_turnaround_days: number
  optimal_submission_strategy: string
}

export interface PresubmissionChecklist {
  required_documents: Array<{ name: string; description: string; is_critical: boolean; reason: string }>
  clinical_evidence_needed: string[]
  prior_therapy_documentation: Array<{ drug: string; min_duration: string }>
  carrier_specific_requirements: string[]
  common_pitfalls: string[]
  appeal_deadline: string
}

export interface P2PPrep {
  talking_points: string[]
  anticipated_objections: Array<{ objection: string; response: string }>
  clinical_evidence_to_cite: string[]
  carrier_specific_tips: string[]
  opening_statement: string
  closing_ask: string
  documentation_to_have_ready: string[]
}

export interface DenialTrends {
  trending_denials: Array<{ carrier: string; drug: string; denial_rate_change: number }>
  carrier_strictness_ranking: Array<{ carrier: string; denial_rate: number }>
  emerging_issues: string[]
  alerts: Array<{ type: string; severity: string; message: string }>
}

export interface FinancialImpact {
  expected_recovery: number
  cost_of_inaction: number
  roi_of_appeal: number
  time_investment_hours: number
  opportunity_cost: number
  recommendation: string
}

export interface CommunityMetrics {
  overall_approval_rate: number
  approval_rate_by_carrier: Record<string, number>
  approval_rate_by_drug_class: Record<string, number>
  top_performing_strategies: string[]
  total_recovered_amount: number
}

export interface CommunityBenchmarks {
  your_approval_rate: number
  community_average: number
  percentile_rank: number
  areas_for_improvement: string[]
}

export interface SmartAlert {
  id: string
  type: string
  severity: 'critical' | 'warning' | 'info'
  title: string
  message: string
  action_required: boolean
  created_at: string
  expires_at: string | null
}

export interface UnappealedDenials {
  total_unappealed_denials: number
  total_unappealed_amount: number
  by_carrier: Record<string, { count: number; amount: number }>
  by_drug: Record<string, { count: number; amount: number }>
  estimated_recoverable: number
  quick_wins: Array<{ carrier: string; drug: string; amount: number; predicted_approval: number }>
  roi_estimate: number
}

export async function apiPredictApproval(data: {
  carrier: string; drug: string; diagnosis_codes: string[];
  plan_type: string; documentation: Record<string, boolean>
}): Promise<ApprovalPrediction> {
  return (await api.post<ApprovalPrediction>('/api/intelligence/predict-approval', data)).data
}

export async function apiPresubmissionChecklist(data: {
  carrier: string; drug: string; diagnosis_codes: string[]
}): Promise<PresubmissionChecklist> {
  return (await api.post<PresubmissionChecklist>('/api/intelligence/presubmission-checklist', data)).data
}

export async function apiIntelligenceP2PPrep(data: {
  carrier: string; drug: string; diagnosis_codes: string[]; denial_reason: string
}): Promise<P2PPrep> {
  return (await api.post<P2PPrep>('/api/intelligence/peer-to-peer-prep', data)).data
}

export async function apiDenialTrends(lookbackDays = 90): Promise<DenialTrends> {
  return (await api.get<DenialTrends>('/api/intelligence/denial-trends', { params: { lookback_days: lookbackDays } })).data
}

export async function apiFinancialImpact(data: {
  carrier: string; drug: string; approval_probability: number; claim_amount: number
}): Promise<FinancialImpact> {
  return (await api.post<FinancialImpact>('/api/intelligence/financial-impact', data)).data
}

export async function apiCommunityMetrics(): Promise<CommunityMetrics> {
  return (await api.get<CommunityMetrics>('/api/intelligence/community-metrics')).data
}

export async function apiCommunityBenchmarks(): Promise<CommunityBenchmarks> {
  return (await api.get<CommunityBenchmarks>('/api/intelligence/community-benchmarks')).data
}

export async function apiTrendingStrategies(carrier: string, drug: string) {
  return (await api.get('/api/intelligence/trending-strategies', { params: { carrier, drug } })).data
}

export async function apiSmartAlerts(): Promise<SmartAlert[]> {
  return (await api.get<SmartAlert[]>('/api/intelligence/alerts')).data
}

export async function apiSystemAlerts(): Promise<SmartAlert[]> {
  return (await api.get<SmartAlert[]>('/api/intelligence/admin/system-alerts')).data
}

export async function apiUnappealedDenials(): Promise<UnappealedDenials> {
  return (await api.get<UnappealedDenials>('/api/intelligence/unappealed-denials')).data
}

export async function apiIntelligenceHealth() {
  return (await api.get('/api/intelligence/health')).data
}

// ── Export the configured axios instance as default ───────────────────────────

export default api
